import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import { ItemNotFound } from "../404/404";
import { getProviders } from "../../services/providers.service";
import { HOME_ROUTERS_PLANS } from "../../../config";
import { HomeRouterPlan } from "../../../types";
import Rs from "../../components/design/rs";
import { routerToItem, trackViewItem } from "../../tools/tracking";
import "./homeRouters.css";

/* ----------------------------------------------------------------------------
 * "Open the card" animation (iOS app-icon → app style).
 *
 * When a compare card is opened we record where the card box and each of its
 * values are on screen (FIRST). After we navigate, the detail page renders the
 * same values in their final layout (LAST). We then run a FLIP: a backdrop
 * grows from the card's rect into the hero panel, and every value element
 * travels from its position on the card to its place in the page.
 * -------------------------------------------------------------------------- */
type Rect = { top: number; left: number; width: number; height: number };

// where each value lives on the detail page (its LAST position)
const FLIP_PARTS: Record<string, string> = {
  name: ".hr-detail__name",
  price: ".hr-detail__media .router-card__price",
  network: ".hr-detail__network",
  provider: ".hr-detail__provider-top",
};

// geometry captured from the clicked card, read once on the next detail render
let pendingFlip: { box: Rect; parts: Record<string, Rect> } | null = null;

const toRect = (r: DOMRect): Rect => ({
  top: r.top,
  left: r.left,
  width: r.width,
  height: r.height,
});

// transform that places `last` back at `first` (origin must be top-left)
const flipTransform = (first: Rect, last: Rect) =>
  `translate(${first.left - last.left}px, ${first.top - last.top}px) scale(${
    last.width ? first.width / last.width : 1
  }, ${last.height ? first.height / last.height : 1})`;

const canAnimate = () =>
  typeof window !== "undefined" &&
  typeof document.body.animate === "function" &&
  !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// called from a compare card: snapshot geometry, then navigate
const openPlanFromCard = (
  navigate: (to: string) => void,
  to: string,
  cardEl: HTMLElement | null
) => {
  if (!cardEl || !canAnimate()) {
    navigate(to);
    return;
  }
  const parts: Record<string, Rect> = {};
  Object.keys(FLIP_PARTS).forEach((key) => {
    const el = cardEl.querySelector(`[data-flip="${key}"]`);
    if (el) parts[key] = toRect(el.getBoundingClientRect());
  });
  pendingFlip = { box: toRect(cardEl.getBoundingClientRect()), parts };
  navigate(to);
};

// run on the detail page once the new plan is in the DOM
const playOpenFlip = () => {
  const pf = pendingFlip;
  pendingFlip = null;
  if (!pf) return;

  const heroBox = document.querySelector<HTMLElement>(".hr-detail__media");
  if (!heroBox) return;

  // animate against the final (top-of-page) layout
  window.scrollTo(0, 0);

  const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
  const duration = 560;
  const lastBox = toRect(heroBox.getBoundingClientRect());

  // backdrop that grows from the card box into the hero panel
  const overlay = document.createElement("div");
  Object.assign(overlay.style, {
    position: "fixed",
    top: `${lastBox.top}px`,
    left: `${lastBox.left}px`,
    width: `${lastBox.width}px`,
    height: `${lastBox.height}px`,
    background: "#fff",
    borderRadius: "20px",
    boxShadow: "0 28px 70px rgba(62, 182, 174, 0.45)",
    transformOrigin: "top left",
    zIndex: "60",
    pointerEvents: "none",
  } as Partial<CSSStyleDeclaration>);
  document.body.appendChild(overlay);
  overlay.animate(
    [
      { transform: flipTransform(pf.box, lastBox), opacity: 1 },
      { transform: "none", opacity: 0 },
    ],
    { duration, easing, fill: "forwards" }
  ).onfinish = () => overlay.remove();

  // each value travels from the card to its place in the layout
  Object.entries(FLIP_PARTS).forEach(([key, sel]) => {
    const first = pf.parts[key];
    const el = document.querySelector<HTMLElement>(sel);
    if (!first || !el) return;
    const last = toRect(el.getBoundingClientRect());
    if (!last.width || !last.height) return;
    const prevOrigin = el.style.transformOrigin;
    el.style.transformOrigin = "top left";
    const anim = el.animate(
      [
        { transform: flipTransform(first, last), opacity: 0.25 },
        { transform: "none", opacity: 1 },
      ],
      { duration, easing, delay: 60, fill: "both" }
    );
    anim.onfinish = () => {
      el.style.transform = "";
      el.style.transformOrigin = prevOrigin;
    };
  });

  // the supporting content settles in just after
  document
    .querySelectorAll<HTMLElement>(".hr-detail__summary, .hr-detail__panel")
    .forEach((el, i) => {
      el.animate(
        [
          { opacity: 0, transform: "translateY(22px)" },
          { opacity: 1, transform: "none" },
        ],
        { duration: 460, easing, delay: 140 + i * 70, fill: "both" }
      );
    });
};

// pick the EN/AR field of a plan based on the active language
const localized = (plan: HomeRouterPlan, key: string, lang: string) =>
  (plan as any)[key + (lang === "ar" ? "Ar" : "En")];

const planNetwork = (plan: HomeRouterPlan) =>
  plan.network || (/5G/i.test(plan.routerTypeEn) ? "5G" : "4G");

const HomeRouterDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const allPlans = HOME_ROUTERS_PLANS as HomeRouterPlan[];
  const index = Number(id);
  const plan =
    !Number.isNaN(index) && index >= 0 && index < allPlans.length
      ? allPlans[index]
      : undefined;

  const [provider, setProvider] = React.useState<any>(undefined);

  React.useEffect(() => {
    if (!plan) return;
    let active = true;
    getProviders(lang)
      .then((res) => {
        if (!active) return;
        const found = (res?.returnData || []).find(
          (p: any) => p.id === plan.providerId
        );
        setProvider(found);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [lang, plan]);

  // play the "open the card" animation right after the new plan is laid out
  React.useLayoutEffect(() => {
    playOpenFlip();
  }, [id]);

  const trackedIndex = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!plan || trackedIndex.current === index) return;
    trackedIndex.current = index;
    trackViewItem(routerToItem(plan, index, lang, provider));
    // provider resolves after this fires; item_brand is best-effort on routers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, plan]);

  if (!plan) {
    return (
      <>
        <div className="home-routers__section">
          <div className="container">
            <Header />
            <ItemNotFound
              title={t("home_routers.no_plans_found")}
              description={t("home_routers.no_plans_description")}
              button={t("home_routers.back_to_routers")}
              onClick={() => navigate("/home-routers")}
            />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const packageDetails: string[] =
    localized(plan, "packageDetails", lang) || [];
  const terms: string[] = localized(plan, "terms", lang) || [];

  return (
    <>
      <div className="home-routers__section">
        <div className="container">
          <Header />

          <Link to="/home-routers" className="hr-detail__back">
            <i
              className={`fas fa-arrow-${lang === "ar" ? "right" : "left"}`}
            />
            <span>{t("home_routers.back_to_routers")}</span>
          </Link>

          <div className="hr-detail">
            <div className="row g-4">
              {/* summary / headline */}
              <div className="col-12 col-lg-5">
                <div className="hr-detail__media">
                  <span className="router-card__network hr-detail__network">
                    {planNetwork(plan)}
                  </span>

                  {localized(plan, "marketingMessage", lang) ? (
                    <span className="router-card__marketing">
                      {localized(plan, "marketingMessage", lang)}
                    </span>
                  ) : null}

                  <h1 className="hr-detail__name">
                    {localized(plan, "name", lang)}
                  </h1>

                  {plan.show === false ? (
                    <div className="router-card__price">
                      <span className="router-card__price-value">
                        {plan.regularPrice}
                      </span>
                      <span className="router-card__price-currency">
                        <Rs size={18} />
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="router-card__price">
                        <span className="router-card__price-value">
                          {plan.discountedPrice}
                        </span>
                        <span className="router-card__price-currency">
                          <Rs size={18} />
                        </span>
                        <span className="router-card__price-note">
                          {t("home_routers.for_first_months", {
                            count: plan.discountDurationMonths,
                          })}
                        </span>
                      </div>
                      <p className="router-card__regular">
                        {t("home_routers.then_regular", {
                          price: plan.regularPrice,
                        })}{" "}
                        <Rs size={14} color="#6b6880" />
                      </p>
                    </>
                  )}

                  <p className="hr-detail__commitment">
                    {t("home_routers.commitment", {
                      count: plan.commitmentPeriodMonths,
                    })}
                  </p>

                  <button
                    className="router-card__cta hr-detail__cta"
                    onClick={() => navigate(`/home-routers/${index}/subscribe`)}
                  >
                    {t("home_routers.subscribe")}
                  </button>
                </div>
              </div>

              {/* specs */}
              <div className="col-12 col-lg-7">
                <div className="hr-detail__summary">
                  {provider ? (
                    <div className="router-card__provider hr-detail__provider-top">
                      {provider.imagePathSmall ? (
                        <img
                          className="router-card__provider-logo"
                          src={provider.imagePathSmall}
                          alt={provider.name}
                        />
                      ) : null}
                      <span className="router-card__provider-name">
                        {provider.name}
                      </span>
                    </div>
                  ) : null}

                  <ul className="router-card__specs hr-detail__specs">
                    <li>
                      <span>{t("home_routers.data")}</span>
                      <strong>{localized(plan, "data", lang)}</strong>
                    </li>
                    <li>
                      <span>{t("home_routers.download_speed")}</span>
                      <strong>{localized(plan, "downloadSpeed", lang)}</strong>
                    </li>
                    <li>
                      <span>{t("home_routers.upload_speed")}</span>
                      <strong>{localized(plan, "uploadSpeed", lang)}</strong>
                    </li>
                    <li>
                      <span>{t("home_routers.router")}</span>
                      <strong>{localized(plan, "routerType", lang)}</strong>
                    </li>
                    <li>
                      <span>{t("home_routers.router_price")}</span>
                      <strong>{localized(plan, "routerPrice", lang)}</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* details + terms */}
            <div className="row g-4 mt-1">
              <div className="col-12 col-lg-6">
                <div className="hr-detail__panel">
                  <h2>{t("home_routers.package_details")}</h2>
                  <ul className="router-card__list">
                    {packageDetails.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="hr-detail__panel">
                  <h2>{t("home_routers.terms")}</h2>
                  <ul className="router-card__list router-card__list--muted">
                    {terms.map((term, i) => (
                      <li key={i}>{term}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* compare slider (last section) — hidden for now */}
            {/* <HomeRouterCompare
              plans={allPlans}
              initialIndex={index}
              lang={lang}
              t={t}
            /> */}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

const CARD_WIDTH = 300;
const CARD_GAP = 24;
const CARD_STEP = CARD_WIDTH + CARD_GAP;

// shared compare-card body, reused by the pinned selected plan and the slider
const CompareCardBody: React.FC<{
  plan: HomeRouterPlan;
  lang: string;
  t: any;
  isSelected: boolean;
  cta: React.ReactNode;
  provider?: any;
}> = ({ plan, lang, t, isSelected, cta, provider }) => (
  <>
    <div className="hr-compare__card-top">
      {isSelected ? (
        <span className="hr-compare__current">
          {t("home_routers.current_plan")}
        </span>
      ) : (
        <span />
      )}
      <span className="router-card__network" data-flip="network">
        {planNetwork(plan)}
      </span>
    </div>

    <h3 className="hr-compare__name" data-flip="name">
      {localized(plan, "name", lang)}
    </h3>

    {provider ? (
      <div className="router-card__provider hr-compare__provider" data-flip="provider">
        {provider.imagePathSmall ? (
          <img
            className="router-card__provider-logo"
            src={provider.imagePathSmall}
            alt={provider.name}
          />
        ) : null}
        <span className="router-card__provider-name">{provider.name}</span>
      </div>
    ) : null}

    <div className="router-card__price" data-flip="price">
      <span className="router-card__price-value">
        {plan.show === false ? plan.regularPrice : plan.discountedPrice}
      </span>
      <span className="router-card__price-currency">
        <Rs size={16} />
      </span>
    </div>
    {plan.show === false ? null : (
      <p className="router-card__regular">
        {t("home_routers.then_regular", {
          price: plan.regularPrice,
        })}{" "}
        <Rs size={14} color="#6b6880" />
      </p>
    )}

    <ul className="router-card__specs hr-compare__specs">
      <li>
        <span>{t("home_routers.data")}</span>
        <strong>{localized(plan, "data", lang)}</strong>
      </li>
      <li>
        <span>{t("home_routers.download_speed")}</span>
        <strong>{localized(plan, "downloadSpeed", lang)}</strong>
      </li>
      <li>
        <span>{t("home_routers.router")}</span>
        <strong>{localized(plan, "routerType", lang)}</strong>
      </li>
      <li>
        <span>{t("home_routers.commitment_short")}</span>
        <strong>{plan.commitmentPeriodMonths}</strong>
      </li>
    </ul>

    {cta}
  </>
);

const HomeRouterCompare: React.FC<{
  plans: HomeRouterPlan[];
  initialIndex: number;
  lang: string;
  t: any;
}> = ({ plans, initialIndex, lang, t }) => {
  const navigate = useNavigate();
  const viewportRef = React.useRef<HTMLDivElement>(null);

  const selectedPlan = plans[initialIndex];

  // providerId -> provider, so cards/filters can show provider names + logos
  const [providers, setProviders] = React.useState<Record<number, any>>({});

  React.useEffect(() => {
    let active = true;
    getProviders(lang)
      .then((res) => {
        if (!active) return;
        const map: Record<number, any> = {};
        (res?.returnData || []).forEach((p: any) => {
          map[p.id] = p;
        });
        setProviders(map);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [lang]);

  // filters
  const [filterProvider, setFilterProvider] = React.useState("all");
  const [priceSort, setPriceSort] = React.useState("none");

  // every other plan (excluding the selected one), keeping its real index
  const baseOthers = React.useMemo(
    () =>
      plans
        .map((plan, idx) => ({ plan, idx }))
        .filter((p) => p.idx !== initialIndex),
    [plans, initialIndex]
  );

  // provider options present among the other plans
  const providerOptions = React.useMemo(() => {
    const ids = Array.from(new Set(baseOthers.map((o) => o.plan.providerId)));
    return ids.map((id) => ({
      id,
      name: providers[id]?.name || `#${id}`,
    }));
  }, [baseOthers, providers]);

  // apply provider filter + price sort
  const others = React.useMemo(() => {
    let list = baseOthers.filter(
      (o) =>
        filterProvider === "all" ||
        o.plan.providerId === Number(filterProvider)
    );
    if (priceSort === "asc") {
      list = [...list].sort(
        (a, b) => a.plan.discountedPrice - b.plan.discountedPrice
      );
    } else if (priceSort === "desc") {
      list = [...list].sort(
        (a, b) => b.plan.discountedPrice - a.plan.discountedPrice
      );
    }
    return list;
  }, [baseOthers, filterProvider, priceSort]);

  const [active, setActive] = React.useState(0);
  const [vpWidth, setVpWidth] = React.useState(0);

  // drag state
  const [dragging, setDragging] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const startX = React.useRef(0);

  // reset to the first card whenever the selection or filters change
  React.useEffect(() => {
    setActive(0);
  }, [initialIndex, filterProvider, priceSort]);

  React.useEffect(() => {
    const update = () => setVpWidth(viewportRef.current?.offsetWidth || 0);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const clamp = (n: number) => Math.max(0, Math.min(others.length - 1, n));
  const go = (n: number) => setActive(clamp(n));

  const onPointerDown = (e: React.PointerEvent) => {
    setDragging(true);
    startX.current = e.clientX;
    setOffset(0);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    setOffset(e.clientX - startX.current);
  };
  const onPointerUp = () => {
    if (!dragging) return;
    if (offset > CARD_STEP / 4) go(active - 1);
    else if (offset < -CARD_STEP / 4) go(active + 1);
    setDragging(false);
    setOffset(0);
  };

  // center the active "other" card in the carousel, plus any live drag delta
  const shift = vpWidth / 2 - CARD_WIDTH / 2 - active * CARD_STEP + offset;

  return (
    <div className="hr-compare">
      <div className="hr-compare__head">
        <h2>{t("home_routers.compare_title")}</h2>
        <p>{t("home_routers.compare_subtitle")}</p>
      </div>

      {/* filters */}
      <div className="hr-compare__filters">
        <select
          className="hr-compare__select"
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value)}
        >
          <option value="all">{t("home_routers.all_providers")}</option>
          {providerOptions.map((p) => (
            <option value={p.id} key={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <select
          className="hr-compare__select"
          value={priceSort}
          onChange={(e) => setPriceSort(e.target.value)}
        >
          <option value="none">{t("home_routers.sort_price")}</option>
          <option value="asc">{t("home_routers.sort_price_asc")}</option>
          <option value="desc">{t("home_routers.sort_price_desc")}</option>
        </select>
      </div>

      <div className="hr-compare__stage">
        {/* selected plan — pinned, always side by side */}
        <div className="hr-compare__selected">
          <div
            className="hr-compare__card is-active"
            dir={lang === "ar" ? "rtl" : "ltr"}
            style={{ width: CARD_WIDTH }}
          >
            <CompareCardBody
              plan={selectedPlan}
              lang={lang}
              t={t}
              isSelected
              provider={providers[selectedPlan.providerId]}
              cta={
                <button
                  type="button"
                  className="router-card__cta hr-compare__cta"
                >
                  {t("home_routers.subscribe")}
                </button>
              }
            />
          </div>
          <span className="hr-compare__vs">{t("home_routers.vs")}</span>
        </div>

        {/* other plans — coverflow carousel */}
        <div className="hr-compare__viewport" ref={viewportRef}>
          <div
            className={`hr-compare__track ${dragging ? "is-dragging" : ""}`}
            style={{ transform: `translateX(${shift}px)` }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            {others.map(({ plan, idx }, i) => {
              const distance = Math.abs(i - active);
              const isActive = i === active;
              const cardStyle: React.CSSProperties = {
                width: CARD_WIDTH,
                transform: `scale(${
                  isActive ? 1 : distance === 1 ? 0.85 : 0.72
                })`,
                opacity: distance === 0 ? 1 : distance === 1 ? 0.6 : 0.3,
                filter: distance >= 2 ? "blur(2px)" : "none",
                zIndex: 100 - distance,
              };
              return (
                <div
                  key={idx}
                  className={`hr-compare__card ${isActive ? "is-active" : ""}`}
                  style={cardStyle}
                  dir={lang === "ar" ? "rtl" : "ltr"}
                  onClick={() => (isActive ? undefined : go(i))}
                >
                  <CompareCardBody
                    plan={plan}
                    lang={lang}
                    t={t}
                    isSelected={false}
                    provider={providers[plan.providerId]}
                    cta={
                      <button
                        type="button"
                        className="router-card__cta hr-compare__cta"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isActive) {
                            const card = (
                              e.currentTarget as HTMLElement
                            ).closest(
                              ".hr-compare__card"
                            ) as HTMLElement | null;
                            openPlanFromCard(
                              navigate,
                              `/home-routers/${idx}`,
                              card
                            );
                          } else {
                            go(i);
                          }
                        }}
                      >
                        {isActive
                          ? t("home_routers.view_details")
                          : t("home_routers.compare")}
                      </button>
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="hr-compare__nav hr-compare__nav--prev"
          onClick={() => go(active - 1)}
          disabled={active === 0}
          aria-label="previous"
        >
          <i className="fas fa-chevron-left" />
        </button>
        <button
          type="button"
          className="hr-compare__nav hr-compare__nav--next"
          onClick={() => go(active + 1)}
          disabled={active === others.length - 1}
          aria-label="next"
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      <div className="hr-compare__dots">
        {others.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`hr-compare__dot ${i === active ? "is-active" : ""}`}
            onClick={() => go(i)}
            aria-label={`plan ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HomeRouterDetail;
